(() => {
  'use strict';

  if (window.CartPageFixedSummary && typeof window.CartPageFixedSummary.initAll === 'function') {
    window.CartPageFixedSummary.initAll();
    return;
  }

  const ROOT_SELECTOR = 'cart-items[data-cart-page-controller]';
  const controllers = new WeakMap();

  class FixedSummaryController {
    constructor(root) {
      this.root = root;
      this.section = root.closest('.shopify-section') || root.parentElement;
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.footerGap = 20;
      this.sidebar = null;
      this.anchor = null;
      this.anchorDocumentTop = Number.POSITIVE_INFINITY;
      this.frame = 0;
      this.refreshTimer = 0;
      this.legacyNoteFields = new Set();

      this.update = this.update.bind(this);
      this.requestUpdate = this.requestUpdate.bind(this);
      this.measure = this.measure.bind(this);
      this.scheduleMeasure = this.scheduleMeasure.bind(this);

      this.observer = new MutationObserver(() => {
        if (
          !this.sidebar ||
          !this.root.contains(this.sidebar) ||
          !this.anchor ||
          !this.root.contains(this.anchor)
        ) {
          this.scheduleMeasure();
        }
      });

      this.observer.observe(this.root, { childList: true, subtree: true });
      window.addEventListener('scroll', this.requestUpdate, { passive: true });
      window.addEventListener('resize', this.scheduleMeasure, { passive: true });
      this.desktopQuery.addEventListener('change', this.scheduleMeasure);
      window.requestAnimationFrame(this.measure);
    }

    disableLegacyNoteFields() {
      document.querySelectorAll('#main-cart-footer textarea[name="note"][form="cart"]').forEach((field) => {
        this.legacyNoteFields.add(field);
        field.disabled = true;
      });
    }

    restoreLegacyNoteFields() {
      this.legacyNoteFields.forEach((field) => {
        if (field.isConnected) field.disabled = false;
      });
      this.legacyNoteFields.clear();
    }

    clearFixedState() {
      if (!this.sidebar) return;
      this.sidebar.classList.remove('is-cart-page-fixed');
      this.sidebar.style.removeProperty('--cart-page-fixed-left');
      this.sidebar.style.removeProperty('--cart-page-fixed-width');
      this.sidebar.style.removeProperty('--cart-page-fixed-top');
      this.sidebar.style.removeProperty('--cart-page-viewport-top');
    }

    getViewportTop() {
      let headerBottom = 0;
      const headers = document.querySelectorAll('sticky-header, #shopify-section-header, .section-header');

      headers.forEach((header) => {
        const rect = header.getBoundingClientRect();
        if (rect.top <= 1 && rect.bottom > 0 && rect.height < window.innerHeight * 0.45) {
          headerBottom = Math.max(headerBottom, rect.bottom);
        }
      });

      return Math.max(20, Math.round(headerBottom + 16));
    }

    update() {
      this.frame = 0;

      if (
        !this.sidebar ||
        !this.anchor ||
        !this.root.contains(this.sidebar) ||
        !this.desktopQuery.matches
      ) {
        this.clearFixedState();
        return;
      }

      const viewportTop = this.getViewportTop();

      if (window.scrollY + viewportTop < this.anchorDocumentTop) {
        this.clearFixedState();
        return;
      }

      if (!this.sidebar.classList.contains('is-cart-page-fixed')) {
        const rect = this.sidebar.getBoundingClientRect();
        this.sidebar.style.setProperty('--cart-page-fixed-left', `${rect.left}px`);
        this.sidebar.style.setProperty('--cart-page-fixed-width', `${rect.width}px`);
        this.sidebar.classList.add('is-cart-page-fixed');
      }

      this.sidebar.style.setProperty('--cart-page-viewport-top', `${viewportTop}px`);
      this.sidebar.style.setProperty('--cart-page-fixed-top', `${viewportTop}px`);

      const footer = document.querySelector('footer.footer, footer, #shopify-section-footer');
      if (!footer) return;

      const footerTop = footer.getBoundingClientRect().top;
      const sidebarHeight = this.sidebar.getBoundingClientRect().height;
      const stopTop = footerTop - sidebarHeight - this.footerGap;
      this.sidebar.style.setProperty('--cart-page-fixed-top', `${Math.min(viewportTop, stopTop)}px`);
    }

    requestUpdate() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(this.update);
    }

    measure() {
      this.clearFixedState();
      this.disableLegacyNoteFields();
      this.sidebar = this.root.querySelector('.cart-page-sidebar');
      this.anchor = this.root.querySelector('.cart-items');

      if (!this.sidebar || !this.anchor || !this.desktopQuery.matches) {
        this.anchorDocumentTop = Number.POSITIVE_INFINITY;
        return;
      }

      const sidebarRect = this.sidebar.getBoundingClientRect();
      const anchorRect = this.anchor.getBoundingClientRect();
      this.sidebar.style.setProperty('--cart-page-fixed-left', `${sidebarRect.left}px`);
      this.sidebar.style.setProperty('--cart-page-fixed-width', `${sidebarRect.width}px`);
      this.anchorDocumentTop = window.scrollY + anchorRect.top;
      this.update();
    }

    scheduleMeasure() {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(this.measure, 80);
    }

    destroy() {
      this.observer.disconnect();
      window.removeEventListener('scroll', this.requestUpdate);
      window.removeEventListener('resize', this.scheduleMeasure);
      this.desktopQuery.removeEventListener('change', this.scheduleMeasure);
      window.clearTimeout(this.refreshTimer);
      if (this.frame) window.cancelAnimationFrame(this.frame);
      this.clearFixedState();
      this.restoreLegacyNoteFields();
      controllers.delete(this.root);
    }
  }

  const initRoot = (root) => {
    if (!controllers.has(root)) controllers.set(root, new FixedSummaryController(root));
  };

  const initAll = (scope = document) => {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  };

  const destroyAll = (scope) => {
    const roots = [];
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      const controller = controllers.get(root);
      if (controller) controller.destroy();
    });
  };

  window.CartPageFixedSummary = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
